import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConsent } from '../context/ConsentContext';
import posthogAnalytics from '../utils/posthogAnalytics';

/**
 * Bridges analytics consent to PostHog initialization/identification.
 *
 * PostHog must never initialize before analytics consent is granted. This
 * also covers the case where a user is already authenticated (session
 * restored by AuthContext) and *then* grants consent later in the same
 * visit — PostHog initializes and identifies the current user at that
 * point, without emitting a `login` event (AuthContext only emits `login`
 * from the real login/signup funnel; identifying here mirrors its session
 * -restore behavior, not a fresh login).
 *
 * When analytics consent is withdrawn, PostHog is opted out and reset so it
 * stops capturing until consent is granted again.
 */
export function useAnalyticsConsentBridge() {
  const { isAuthenticated, user } = useAuth();
  const { consent } = useConsent();
  const wasAllowedRef = useRef(false);

  useEffect(() => {
    if (consent.analytics) {
      posthogAnalytics.initialize();
      if (isAuthenticated && user) {
        posthogAnalytics.identifyUser(user);
      }
      wasAllowedRef.current = true;
    } else if (wasAllowedRef.current) {
      posthogAnalytics.disable();
      wasAllowedRef.current = false;
    }
  }, [consent.analytics, isAuthenticated, user]);
}

export default useAnalyticsConsentBridge;
